import { SPLICE_MIGRATION_OPTIONAL_MIN_VERSION } from 'app/constants';

type SemverParts = [number, number, number];

const parseSemverCore = (version: string): SemverParts | null => {
  const trimmed = version.trim();
  if (!trimmed) return null;

  const core = trimmed.split('-')[0];
  const parts = core.split('.').map((part) => Number.parseInt(part, 10));

  if (parts.length < 3 || parts.some((part) => Number.isNaN(part))) {
    return null;
  }

  return [parts[0], parts[1], parts[2]];
};

export const compareSemver = (a: string, b: string): -1 | 0 | 1 => {
  const aParts = parseSemverCore(a);
  const bParts = parseSemverCore(b);

  if (!aParts || !bParts) {
    return 0;
  }

  for (let i = 0; i < 3; i += 1) {
    if (aParts[i] < bParts[i]) return -1;
    if (aParts[i] > bParts[i]) return 1;
  }

  return 0;
};

export const isSpliceVersionGte = (imageTag: string | undefined, minVersion: string): boolean => {
  if (!imageTag?.trim()) return false;
  return compareSemver(imageTag, minVersion) >= 0;
};

export const isMigrationIdOptionalForImageTag = (imageTag: string | undefined): boolean =>
  isSpliceVersionGte(imageTag, SPLICE_MIGRATION_OPTIONAL_MIN_VERSION);

const parseMigrationId = (migrationId: unknown): number | undefined => {
  if (migrationId === '' || migrationId === undefined || migrationId === null) {
    return undefined;
  }

  const parsed = Number(migrationId);
  if (Number.isNaN(parsed)) {
    return undefined;
  }

  return parsed;
};

export const shouldShowMigrationIdInSummary = (imageTag: string | undefined, migrationId: unknown): boolean => {
  if (!isMigrationIdOptionalForImageTag(imageTag)) {
    return true;
  }

  return parseMigrationId(migrationId) !== undefined;
};

/**
 * Omit migration on create for Splice >= 0.6.8 only when id is left empty.
 * Explicit 0 is a valid value and is sent as { id: 0 }.
 */
export const normalizeMigrationForCreateSubmit = (
  imageTag: string | undefined,
  migrationId: unknown,
): { id: number } | undefined => {
  const parsedId = parseMigrationId(migrationId);

  if (isMigrationIdOptionalForImageTag(imageTag)) {
    if (parsedId === undefined) {
      return undefined;
    }

    return { id: parsedId };
  }

  return { id: parsedId ?? 0 };
};
